import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  StyleSheet,
} from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import CratePackingSection from "../components/CratePackingSection";
import CrateScannerSheet from "@components/ds/CrateScannerSheet";
import MarkingSheet from "../components/MarkingSheet";
import UnpackConfirmSheet from "../components/UnpackConfirmSheet";
import useCrateData from "../hooks/useCrateData";
import useCrateSearch from "../hooks/useCrateSearch";
import usePackUnpack from "../hooks/usePackUnpack";
import useMarking from "../hooks/useMarking";
import { tokens, sharedStyles } from "../styles";

// Crate-wise Packing — mirrors web's Customer Crate Items page on mobile.
// Executive types or scans a crate id, hits Fetch, and the page renders
// the crate's items plus its connecting crates (server scopes connecting
// items to the current crate's customer list). Pack Visible Items and
// Mark Issue use the same kernels as the DB-card-driven flow, so writes
// land in the same rows — DB-wise progress on the sibling tab updates
// when this tab fires its parent reload.
//
// State machine:
//   - submittedCrateId === null  → empty state with input + scan + Today/Resch tabs
//   - submittedCrateId set       → useCrateData fetches; render header + sections
//   - Fetch button validates input is non-empty before submission
//
// `parentReload` is the parent screen's reload (refreshes the DB-wise
// snapshot). We chain it after our own crate-detail reload so DB cards
// update when the executive returns to that tab.

const CratePackingTab = ({ parentReload }) => {
  const [tab, setTab] = useState("today");
  const [crateInput, setCrateInput] = useState("");
  const [submittedCrateId, setSubmittedCrateId] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [markTarget, setMarkTarget] = useState(null);
  const [unpackTarget, setUnpackTarget] = useState(null);

  const {
    data: crateData,
    loading,
    reload: reloadCrate,
  } = useCrateData({ crateId: submittedCrateId || "", tab });

  const combinedReload = useCallback(async () => {
    await reloadCrate();
    if (parentReload) await parentReload();
  }, [reloadCrate, parentReload]);

  const { pack, unpack, packing, unpacking } = usePackUnpack({
    reload: combinedReload,
  });
  const {
    submitMarking,
    removeMarking,
    submitting: marking,
    isProcessing,
  } = useMarking({ reload: combinedReload });

  // Auto-select-on-single-match: if the user types a unique suffix, the
  // search returns exactly one crate_id; we use it as the submitted id
  // when they hit Fetch (or scan).
  const handleAutoSelect = useCallback((picked) => {
    if (picked?.crate_id) setCrateInput(String(picked.crate_id));
  }, []);
  const { results: searchResults, search } = useCrateSearch({
    tab,
    onAutoSelect: handleAutoSelect,
  });

  const handleSearchChange = (text) => {
    setCrateInput(text);
    search(text);
  };

  const handleFetch = useCallback(() => {
    const id = String(crateInput || "").trim();
    if (!id) return;
    // If the user typed a partial suffix that has a unique match, prefer
    // that exact crate_id (search results land first).
    const exact = (searchResults || []).find(
      (r) => String(r?.crate_id || "") === id,
    );
    const single =
      searchResults && searchResults.length === 1 ? searchResults[0] : null;
    setSubmittedCrateId(String(exact?.crate_id || single?.crate_id || id));
  }, [crateInput, searchResults]);

  const handleScanDecode = useCallback((decoded) => {
    setScannerOpen(false);
    if (!decoded) return;
    // Pass the decoded string straight through — the search endpoint
    // accepts both crate IDs (e.g. "CP-00366717", which we must NOT
    // split on '-') and AWB numbers. Mirrors the DB-card scanner flow
    // in PackingInterfaceScreen.
    const id = String(decoded).trim();
    if (!id) return;
    setCrateInput(id);
    setSubmittedCrateId(id);
  }, []);

  const handleMarkSelected = useCallback(
    (item) => {
      if (!item) return;
      if (item.is_issue) {
        removeMarking(item);
        return;
      }
      setMarkTarget(item);
    },
    [removeMarking],
  );

  const sections = useMemo(
    () => (Array.isArray(crateData?.crate_data) ? crateData.crate_data : []),
    [crateData],
  );

  const submittedId = submittedCrateId ? String(submittedCrateId) : null;
  const cxList = Array.isArray(crateData?.cxs) ? crateData.cxs : [];
  const totalItemCount = Number(crateData?.total_item_count || 0);
  const unpackedItemCount = Number(crateData?.unpacked_item_count || 0);
  const customerCount = cxList.length;
  const locality = crateData?.cx_locality || "";

  return (
    <View style={styles.wrap}>
      <View style={styles.tabBar}>
        {[
          { key: "today", label: "Today" },
          { key: "rescheduled", label: "Rescheduled" },
        ].map((t) => {
          const isActive = t.key === tab;
          return (
            <TouchableOpacity
              key={t.key}
              activeOpacity={0.85}
              onPress={() => setTab(t.key)}
              style={[styles.tabBtn, isActive && styles.tabBtnActive]}
            >
              <Text
                variant="caption_2"
                style={[
                  styles.tabBtnText,
                  isActive ? styles.tabBtnTextActive : styles.tabBtnTextIdle,
                ]}
              >
                {t.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={styles.searchBar}>
        <TextInput
          value={crateInput}
          onChangeText={handleSearchChange}
          placeholder="Enter crate ID…"
          placeholderTextColor={tokens.textTertiary}
          style={styles.searchInput}
          returnKeyType="search"
          onSubmitEditing={handleFetch}
          testID="crate-packing-search-input"
        />
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={handleFetch}
          style={styles.fetchBtn}
          disabled={!String(crateInput || "").trim()}
        >
          <Text variant="caption_2" style={styles.fetchBtnText}>
            Fetch
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => setScannerOpen(true)}
          style={styles.scanBtn}
        >
          <Text variant="caption_2" style={styles.scanBtnText}>
            Scan
          </Text>
        </TouchableOpacity>
      </View>
      <ScrollView contentContainerStyle={styles.scroll}>
        {!submittedId ? (
          <View style={styles.emptyHero}>
            <Text variant="caption_2" style={styles.emptyHeroText}>
              Enter a crate ID and hit Fetch — or scan a crate to load it.
            </Text>
            {Array.isArray(searchResults) && searchResults.length > 0 ? (
              <View style={styles.suggestions}>
                <Text variant="caption_2" style={styles.suggestionLabel}>
                  Matches
                </Text>
                {searchResults.slice(0, 8).map((r) => (
                  <TouchableOpacity
                    key={String(r?.crate_id)}
                    activeOpacity={0.85}
                    onPress={() => {
                      setCrateInput(String(r.crate_id));
                      setSubmittedCrateId(String(r.crate_id));
                    }}
                    style={styles.suggestionChip}
                  >
                    <Text variant="caption_2" style={styles.suggestionText}>
                      {r.crate_id}
                      {r?.type ? ` · ${r.type}` : ""}
                      {Number(r?.userCount) > 0 ? ` · ${r.userCount} cust` : ""}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : null}
          </View>
        ) : loading && !crateData ? (
          <View style={sharedStyles.centered}>
            <ActivityIndicator color={color.mediumBlack} />
          </View>
        ) : !crateData ? (
          <Text variant="caption_2" style={styles.empty}>
            No crate found for &quot;{submittedId}&quot; under {tab}.
          </Text>
        ) : (
          <>
            <View style={[sharedStyles.card, styles.summary]}>
              <Text variant="title_16" style={styles.summaryTitle}>
                {submittedId}
              </Text>
              {locality ? (
                <Text variant="caption_2" style={styles.summaryMeta}>
                  Locality · {locality}
                </Text>
              ) : null}
              <View style={styles.summaryStatsRow}>
                <Text variant="caption_2" style={styles.summaryStat}>
                  {customerCount} customer{customerCount === 1 ? "" : "s"}
                </Text>
                <Text variant="caption_2" style={styles.summaryStat}>
                  {totalItemCount} total
                </Text>
                <Text variant="caption_2" style={styles.summaryStatWarn}>
                  {unpackedItemCount} unpacked
                </Text>
              </View>
              {cxList.length > 0 ? (
                <View style={styles.chipsRow}>
                  {cxList.map((cx, idx) => (
                    <View
                      key={`${cx?.name || idx}`}
                      style={[
                        styles.chip,
                        cx?.packed ? styles.chipPacked : styles.chipPending,
                      ]}
                    >
                      <Text variant="caption_2" style={styles.chipText}>
                        {cx?.name || "—"}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
            {sections.length === 0 ? (
              <Text variant="caption_2" style={styles.empty}>
                No items in this crate.
              </Text>
            ) : (
              sections.map((s, idx) => (
                <CratePackingSection
                  key={String(s?.crate_id || idx)}
                  section={s}
                  isCurrent={idx === 0 || String(s?.crate_id) === submittedId}
                  packing={packing}
                  unpacking={unpacking}
                  onPack={pack}
                  onUnpack={(target) => setUnpackTarget(target)}
                  onPressMark={handleMarkSelected}
                  isProcessing={isProcessing}
                />
              ))
            )}
          </>
        )}
      </ScrollView>
      <CrateScannerSheet
        visible={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDecode={handleScanDecode}
      />
      <MarkingSheet
        visible={Boolean(markTarget)}
        item={markTarget}
        submitting={marking}
        onClose={() => setMarkTarget(null)}
        onSubmit={async (payload) => {
          const ok = await submitMarking(payload);
          if (ok) setMarkTarget(null);
        }}
      />
      <UnpackConfirmSheet
        visible={Boolean(unpackTarget)}
        crate={unpackTarget}
        unpacking={unpacking}
        onClose={() => setUnpackTarget(null)}
        onConfirm={async () => {
          const ok = await unpack(unpackTarget);
          if (ok) setUnpackTarget(null);
        }}
      />
    </View>
  );
};

export default CratePackingTab;

const styles = StyleSheet.create({
  wrap: { flex: 1 },
  tabBar: {
    flexDirection: "row",
    backgroundColor: tokens.surface,
    paddingHorizontal: tokens.spacing.large,
    paddingTop: tokens.spacing.small,
  },
  tabBtn: {
    flex: 1,
    paddingVertical: 10,
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: tokens.transparent,
  },
  tabBtnActive: { borderBottomColor: tokens.primary.bg },
  tabBtnText: { fontSize: 13, fontWeight: "600" },
  tabBtnTextActive: { color: tokens.primary.bg, fontWeight: "700" },
  tabBtnTextIdle: { color: tokens.textSecondary },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: tokens.surface,
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  searchInput: {
    flex: 1,
    backgroundColor: tokens.surfaceHover,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 8,
    fontSize: 14,
    color: tokens.textPrimary,
  },
  fetchBtn: {
    marginLeft: tokens.spacing.small,
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 8,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.primary.bg,
  },
  fetchBtnText: { color: tokens.primary.fg, fontSize: 13, fontWeight: "700" },
  scanBtn: {
    marginLeft: tokens.spacing.small,
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 8,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.primary.bg,
  },
  scanBtnText: { color: tokens.primary.bg, fontSize: 13, fontWeight: "700" },
  scroll: {
    paddingTop: tokens.spacing.medium,
    paddingBottom: tokens.spacing.xlarge,
  },
  emptyHero: {
    paddingHorizontal: tokens.spacing.page,
    paddingVertical: tokens.spacing.xlarge,
    alignItems: "center",
  },
  emptyHeroText: {
    color: tokens.textTertiary,
    fontSize: 12,
    textAlign: "center",
  },
  suggestions: {
    marginTop: tokens.spacing.large,
    width: "100%",
  },
  suggestionLabel: {
    color: tokens.textSecondary,
    fontSize: 11,
    fontWeight: "700",
    letterSpacing: 0.5,
    marginBottom: tokens.spacing.small,
  },
  suggestionChip: {
    paddingVertical: 10,
    paddingHorizontal: tokens.spacing.medium,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: tokens.borderLight,
    marginBottom: 6,
  },
  suggestionText: { color: tokens.textPrimary, fontSize: 13 },
  empty: {
    color: tokens.textTertiary,
    fontSize: 12,
    textAlign: "center",
    paddingVertical: tokens.spacing.large,
  },
  summary: {
    marginHorizontal: tokens.spacing.page,
    paddingVertical: tokens.spacing.large,
    marginBottom: tokens.spacing.medium,
  },
  summaryTitle: {
    color: tokens.textPrimary,
    fontWeight: "700",
    fontSize: 16,
  },
  summaryMeta: { color: tokens.textSecondary, fontSize: 12, marginTop: 4 },
  summaryStatsRow: {
    flexDirection: "row",
    marginTop: tokens.spacing.medium,
  },
  summaryStat: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginRight: tokens.spacing.medium,
  },
  summaryStatWarn: {
    color: tokens.warning,
    fontSize: 12,
    fontWeight: "700",
  },
  chipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: tokens.spacing.medium,
  },
  chip: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 6,
    borderWidth: StyleSheet.hairlineWidth,
  },
  chipPending: {
    backgroundColor: tokens.surface,
    borderColor: tokens.primary.bg,
  },
  chipPacked: {
    backgroundColor: tokens.surfaceHover,
    borderColor: tokens.borderLight,
  },
  chipText: { color: tokens.textPrimary, fontSize: 11, fontWeight: "600" },
});
